import React, { useState } from 'react';
import { Button, Spinner } from 'reactstrap';
import { useMutation } from '@apollo/client/react';
import { useAuth } from '../../contexts/AuthContext';
import {
  UPDATE_ORDER_STATUS,
  UpdateOrderStatusData,
  UpdateOrderStatusVariables,
} from '../../graphql/mutations/orders';
import { canCustomerRequestCancel } from '../../utils/customerAccess';

interface CustomerCancelRequestButtonProps {
  orderId: number;
  status?: string | null;
  size?: 'sm';
}

const CustomerCancelRequestButton: React.FC<CustomerCancelRequestButtonProps> = ({
  orderId,
  status,
  size,
}) => {
  const { isCustomerPortalUser } = useAuth();
  const [error, setError] = useState<string | null>(null);

  const [updateStatus, { loading }] = useMutation<
    UpdateOrderStatusData,
    UpdateOrderStatusVariables
  >(UPDATE_ORDER_STATUS, {
    refetchQueries: ['GetOrders', 'GetCustomerOrders', 'GetOrder'],
    awaitRefetchQueries: true,
  });

  if (!isCustomerPortalUser() || !canCustomerRequestCancel(status)) {
    return null;
  }

  const onClick = async () => {
    const confirmed = window.confirm(
      `Request cancellation for order #${orderId}? Staff will review this request.`
    );
    if (!confirmed) return;

    setError(null);
    try {
      await updateStatus({
        variables: { orderId, status: 'CANCEL_REQUEST' },
      });
    } catch (err: any) {
      const gqlMessage =
        err?.graphQLErrors?.[0]?.message ||
        err?.networkError?.result?.errors?.[0]?.message;
      setError(gqlMessage || err?.message || 'Unable to send cancel request.');
    }
  };

  return (
    <div>
      <Button color="danger" outline size={size} disabled={loading} onClick={onClick}>
        {loading ? <Spinner size="sm" /> : 'Cancel request'}
      </Button>
      {error ? <div className="text-danger small mt-1">{error}</div> : null}
    </div>
  );
};

export default CustomerCancelRequestButton;
